import { GraphQLClient } from "graphql-request";
import * as Sentry from "@sentry/react";
import { auth } from "@/config/firebase";
import { config } from "@/config/env";
import { generateTraceparent } from "./correlation";
import {
  isSessionExpiredError,
  handleSessionExpiry,
} from "./session-handler";

/**
 * GraphQL client configured with automatic authentication and organization context.
 * Automatically includes Firebase auth token and organization ID headers in all requests.
 * Also handles session expiry by detecting 401 errors and signing out the user.
 *
 * Targets the LB-fronted `/v1/core/graphql` path. In cloud envs, `config.apiUrl`
 * is the LB hostname (e.g. https://api.taskeng.ai); the LB URL map routes
 * `/v1/core/*` to the core backend. In dev, `config.apiUrl` is empty — we
 * prepend `window.location.origin` so the URL parses (graphql-request 7.x
 * validates the endpoint via `new URL(...)` which rejects relative paths).
 * The pathname is unchanged so Vite's `/v1/core` proxy still picks it up.
 */
function resolveGraphqlUrl(): string {
  const path = `${config.apiUrl}/v1/core/graphql`;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  // SSR / test fallback. Not exercised at runtime in the SPA.
  return `http://localhost${path}`;
}

const GRAPHQL_URL = resolveGraphqlUrl();

export const graphqlClient = new GraphQLClient(
  GRAPHQL_URL,
  {
    requestMiddleware: async (request) => {
      const headers = request.headers as Headers;
      const currentOrgId = sessionStorage.getItem("currentOrganizationId");

      // Only set authorization if not already provided (allows manual override)
      if (!headers.has("authorization")) {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }
      }

      if (currentOrgId && currentOrgId !== "all") {
        headers.set("x-organization-id", currentOrgId);
      }

      // W3C Trace Context: trace-id is the per-action trace (route-scoped),
      // span-id is fresh per request. Backend extracts trace-id and uses it
      // as the request-scoped correlation ID. See lib/correlation.ts.
      headers.set("traceparent", generateTraceparent());

      Sentry.addBreadcrumb({
        category: "graphql",
        message: "GraphQL request",
        level: "info",
        data: { url: GRAPHQL_URL },
      });

      return request;
    },
    responseMiddleware: (response) => {
      // Handle session expiry from GraphQL responses.
      //
      // The hardcoded-401 bypass that used to live here false-positived on
      // any GraphQL error whose message mentioned a session-expiry pattern
      // string (e.g., the field name `lastActivityAt`) even when the
      // underlying HTTP status was 200 — graphql-request throws ClientError
      // on 200-with-errors[] responses, and a query that selects a
      // `lastActivityAt` field tripped the matcher whenever that field's
      // resolver errored. Now we check the actual HTTP status on the
      // ClientError instead of assuming 401.
      if (response instanceof Error) {
        const errorMessage = response.message || "";
        const status =
          (response as unknown as { response?: { status?: number } })
            .response?.status ?? 0;
        if (
          status === 401 &&
          (errorMessage.includes("401") ||
            isSessionExpiredError(status, errorMessage))
        ) {
          handleSessionExpiry();
        }
      }
    },
  },
);
